/** W82 (E3, plan-w82.md 3.4): die vier Einstellungen der W82-Welt, lokal je Gerät in localStorage. Unbekannte Werte
 *  gelten als Vorgabe. `weltAnwenden()` setzt die Attribute an <html> (tokens.css, Block W82 WELT); ein Wechsel in
 *  einem anderen Fenster kommt über das `storage`-Ereignis an. Die Erscheinung (Hell, Dunkel) bleibt bei darstellung.ts. */
import { useSyncExternalStore } from "react";

export type Fassung = "lebhaft" | "ruhig";
export type Palette = "nachtblau" | "tintenblau";
export type Marke = "palette" | "fest";
export type Praesenz = "ueberall" | "erklaeren" | "keine";
export type Welt = { fassung: Fassung; palette: Palette; marke: Marke; praesenz: Praesenz };

export const WELT_WERTE = {
  fassung: ["lebhaft", "ruhig"],
  palette: ["nachtblau", "tintenblau"],
  marke: ["palette", "fest"],
  praesenz: ["ueberall", "erklaeren", "keine"],
} as const satisfies { [K in keyof Welt]: readonly Welt[K][] };

/** Vorgaben (E4, FW2: lebhaft) */
export const WELT_VORGABE: Welt = { fassung: "lebhaft", palette: "nachtblau", marke: "palette", praesenz: "ueberall" };

/** Schlüssel in localStorage, gleich benannt wie auf iOS (`@AppStorage`). */
export const WELT_SCHLUESSEL = {
  fassung: "pg-fassung",
  palette: "pg-palette",
  marke: "pg-marke",
  praesenz: "pg-praesenz",
} as const satisfies Record<keyof Welt, string>;

const HINWEIS_PRAESENZ = "pg-hinweis-praesenz";

function lesen(schluessel: string): string | null {
  try {
    return localStorage.getItem(schluessel);
  } catch {
    return null;
  }
}

function wert<K extends keyof Welt>(k: K): Welt[K] {
  const roh = lesen(WELT_SCHLUESSEL[k]);
  return (WELT_WERTE[k] as readonly string[]).includes(roh ?? "") ? (roh as Welt[K]) : WELT_VORGABE[k];
}

export function weltLesen(): Welt {
  return { fassung: wert("fassung"), palette: wert("palette"), marke: wert("marke"), praesenz: wert("praesenz") };
}

/** Setzt data-welt, data-fassung, data-palette, data-marke und data-praesenz an <html>. */
export function weltAnwenden(w: Welt = weltLesen()): void {
  const wurzel = document.documentElement;
  // W82 Teil 4 (Punkt 5): die W82-Gestalt gilt global; `WeltWurzel` bleibt als Kontext der Seiten (Grund, Portale)
  wurzel.setAttribute("data-welt", "app");
  wurzel.setAttribute("data-fassung", w.fassung);
  wurzel.setAttribute("data-palette", w.palette);
  wurzel.setAttribute("data-marke", w.marke);
  wurzel.setAttribute("data-praesenz", w.praesenz);
  // Prüfschalter „still“ (E9): alles sofort im Endzustand, auch die CSS-Schlüsselbilder (welt.css)
  wurzel.toggleAttribute("data-welt-still", typeof location !== "undefined" && new URLSearchParams(location.search).has("still"));
}

const hoerer = new Set<() => void>();
function melden() {
  for (const h of hoerer) h();
}

/** Schreibt die genannten Werte (unbekannte werden verworfen), wendet sie an und meldet sie den Ansichten. */
export function weltSchreiben(teil: Partial<Welt>): void {
  for (const k of Object.keys(teil) as (keyof Welt)[]) {
    const neu = teil[k];
    if (neu === undefined || !(WELT_WERTE[k] as readonly string[]).includes(neu)) continue;
    try {
      localStorage.setItem(WELT_SCHLUESSEL[k], neu);
    } catch {
      /* ohne Speicher gilt die Wahl nur bis zum Neuladen */
    }
  }
  weltAnwenden();
  melden();
}

let zuletzt: Welt = WELT_VORGABE;
function stand(): Welt {
  const w = weltLesen();
  if (w.fassung !== zuletzt.fassung || w.palette !== zuletzt.palette || w.marke !== zuletzt.marke || w.praesenz !== zuletzt.praesenz) zuletzt = w;
  return zuletzt;
}

function abonnieren(h: () => void): () => void {
  hoerer.add(h);
  // ein anderes Fenster hat geschrieben: dort ist die Wahl schon angewandt, hier noch nicht
  const fremd = (e: StorageEvent) => {
    if (e.key !== null && !(Object.values(WELT_SCHLUESSEL) as string[]).includes(e.key)) return;
    weltAnwenden();
    h();
  };
  window.addEventListener("storage", fremd);
  return () => {
    hoerer.delete(h);
    window.removeEventListener("storage", fremd);
  };
}

/** Die Welt als Zustand einer Ansicht; folgt auch Änderungen aus anderen Fenstern. */
export function useWelt(): [Welt, (teil: Partial<Welt>) => void] {
  const w = useSyncExternalStore(abonnieren, stand, () => WELT_VORGABE);
  return [w, weltSchreiben];
}

/** E13: ob der Präsenz-Hinweis schon erschienen ist (Merker lokal). */
export function hinweisPraesenzGesehen(): boolean {
  return lesen(HINWEIS_PRAESENZ) === "gesehen";
}

export function hinweisPraesenzMerken(): void {
  try {
    localStorage.setItem(HINWEIS_PRAESENZ, "gesehen");
  } catch {
    /* ohne Speicher erscheint er beim nächsten Start noch einmal */
  }
}

/** FW7 (Vorlage R11 „einstellungen“): Untertitel der Zeile „Darstellung“ mit der aktuellen Wahl, etwa „Nachtblau, lebhaft,
 *  Überall“. */
export function darstellungWahl(w: Welt): string {
  const palette = { nachtblau: "Nachtblau", tintenblau: "Tintenblau" }[w.palette];
  const praesenz = { ueberall: "Überall", erklaeren: "Nur bei Erklärungen", keine: "Keine Erklärungen" }[w.praesenz];
  return `${palette}, ${w.fassung}, ${praesenz}`;
}

/** Web-5d K9: verschiedene Besuchstage, keine Zahl von Aufrufen und keine verstrichenen Tage. Die Startseite zählt beim
 *  Einhängen; der Präsenz-Hinweis kommt frühestens am dritten Tag (Posteingang.tsx). */
const STARTSEITE_TAGE = "pg-startseite-tage";
type StartseitenStand = { fassung: 1; tage: string[] };

function startseitenStand(): StartseitenStand {
  try {
    const roh: unknown = JSON.parse(lesen(STARTSEITE_TAGE) ?? "null");
    if (!roh || typeof roh !== "object" || !("fassung" in roh) || roh.fassung !== 1 || !("tage" in roh) || !Array.isArray(roh.tage)) {
      return { fassung: 1, tage: [] };
    }
    if (!roh.tage.every((t): t is string => {
      if (typeof t !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(t)) return false;
      const d = new Date(`${t}T12:00:00Z`);
      return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === t;
    })) return { fassung: 1, tage: [] };
    return { fassung: 1, tage: [...new Set(roh.tage)] };
  } catch {
    return { fassung: 1, tage: [] };
  }
}

function berlinerTag(): string {
  const teile = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  return ["year", "month", "day"].map((teil) => teile.find((t) => t.type === teil)?.value).join("-");
}

/** Beim Einhängen der Startseite aufrufen. Auch doppelte Effekte zählen denselben Tag nur einmal. */
export function startseiteTagZaehlen(): void {
  const stand = startseitenStand();
  const tag = berlinerTag();
  if (stand.tage.includes(tag)) return;
  try {
    localStorage.setItem(STARTSEITE_TAGE, JSON.stringify({ fassung: 1, tage: [...stand.tage, tag] }));
  } catch {
    /* Ohne lokalen Speicher wird kein Besuchstag behauptet. */
  }
}

/** Anzahl verschiedener Tage mit Startseitenbesuch; unbekannte Speicherwerte gelten als 0. */
export function startseitenTage(): number {
  return startseitenStand().tage.length;
}

/** Wie `startseitenTage()`, der heutige Tag zählt mit, auch wenn er noch nicht gespeichert ist; schreibt nichts (für den
 *  Anfangszustand einer Ansicht, die beim Einhängen zählt). */
export function startseitenTageMitHeute(): number {
  const stand = startseitenStand();
  return stand.tage.length + (stand.tage.includes(berlinerTag()) ? 0 : 1);
}

/** W82 Web-5h2 (Frage 44 = A, D-621, FW20): die volle Begrüßung der Startseite nur beim ersten Öffnen des Kalendertags
 *  (Europe/Berlin) je Konto, sonst der kurze ruhige Wechsel. Merker lokal je Gerät, Schlüssel `pg-auftritt-tag:<nutzer_id>`,
 *  Wert der Tag (`heuteBerlin`). Mehrere Tabs am selben Tag: der erste schreibt, die anderen wechseln ruhig. */
const AUFTRITT_TAG = "pg-auftritt-tag:";
/** Rückfall, solange das Konto beim ersten Rendern nicht bekannt ist: der Merker des Besuchs wie bis 5h (E29). */
const AUFTRITT_BESUCH = "pg-auftritt-start";

/** „voll“, wenn für dieses Konto heute noch keine volle Begrüßung lief (neuer Tag oder anderes Konto), sonst „wechsel“.
 *  Ohne Konto gilt der Merker des Besuchs (sessionStorage) und wird gleich gesetzt; ohne Speicher „wechsel“ (wie bisher). */
export function auftrittHeute(konto: string | null | undefined, heute: string): "voll" | "wechsel" {
  try {
    if (!konto) {
      const erst = sessionStorage.getItem(AUFTRITT_BESUCH) === null;
      sessionStorage.setItem(AUFTRITT_BESUCH, "1");
      return erst ? "voll" : "wechsel";
    }
    return localStorage.getItem(AUFTRITT_TAG + konto) === heute ? "wechsel" : "voll";
  } catch {
    return "wechsel";
  }
}

/** Merkt die volle Begrüßung des Tags für das Konto (beim Einhängen der Startseite, nicht im Rendern). */
export function auftrittMerken(konto: string, heute: string): void {
  try {
    localStorage.setItem(AUFTRITT_TAG + konto, heute);
  } catch {
    /* ohne Speicher begrüßt die Startseite beim nächsten Öffnen wieder voll */
  }
}

/** Web-5d K9: ein Besuch (Sitzung des Tabs), in dem die Einführung lief; die Startseite bietet den Präsenz-Hinweis dann
 *  nicht an (dort fragt 5g die Fassung). sessionStorage endet mit dem Tab, ein Neuladen bleibt derselbe Besuch. */
const EINFUEHRUNG_BESUCH = "pg-einfuehrung-besuch";

export function einfuehrungImBesuchMerken(): void {
  try {
    sessionStorage.setItem(EINFUEHRUNG_BESUCH, "1");
  } catch {
    /* ohne Speicher gilt der Besuch als einer ohne Einführung */
  }
}

export function einfuehrungImBesuch(): boolean {
  try {
    return sessionStorage.getItem(EINFUEHRUNG_BESUCH) === "1";
  } catch {
    return false;
  }
}
